// Librairies
import i18n from '../locales/i18n';
import { faCandyCane, faChair, faBan, faLampStreet, faPump, faQuestion, faTablePicnic, faTankWater, faTeddyBear, faTreeDecorated, faBoxTaped, faTrash, faSignsPost, faFeather, faSignHanging } from '@fortawesome/pro-solid-svg-icons';
import { isMobile } from 'react-device-detect';
import tinycolor from 'tinycolor2';
// Utils
import ClassificationUtil from './ClassificationUtil';
import PatternsUtil from './PatternsUtil';
import TreesUtil from './TreesUtil';
import FormulasUtil from './FormulasUtil';

const SCALE_ZOOM_LEVEL = 17;

export default class StylesUtil {
    static getMissingDataColor = () => ClassificationUtil.getMissingDataColor();

    static getBorderColor(style) {
        if (style?.color !== '#000') return style?.color;
        const fillColor = style.fillColor?.includes('var(') ? getComputedStyle(document.documentElement).getPropertyValue(style.fillColor.replace('var(', '').replace(')', '')) : style.fillColor;
        return tinycolor(fillColor).getBrightness() < 100 ? '#fff' : '#000';
    }

    static getCustomStyleValue = (style, fieldList, properties) => ClassificationUtil.getThematicMapStyleValue(style, fieldList, properties);

    /* Arbres */
    // Couleurs
    static getHealthReviewColor = (healthReview) => ClassificationUtil.getHealthReviewColor(healthReview);

    static getVigorColor = (vigor) => ClassificationUtil.getVigorColor(vigor);

    static getRiskColor = (risk) => ClassificationUtil.getRiskColor(risk);

    static getAccurateRiskColor = (accurateRisk) => ClassificationUtil.getAccurateRiskColor(accurateRisk);

    static getOntogenicStageColor = (ontogenicStage) => ClassificationUtil.getOntogenicStageColor(ontogenicStage);

    static getArchiStageColor = (archiStage) => ClassificationUtil.getArchiStageColor(archiStage);

    static getArchiStateColor = (archiState) => ClassificationUtil.getArchiStateColor(archiState);

    static getCutDownColor = (toCutDown) => ClassificationUtil.getCutDownColor(toCutDown);

    static getActionsUrgencyColor = (actionsUrgency) => ClassificationUtil.getActionsUrgencyColor(actionsUrgency);

    static getStabilityColor = (stability) => ClassificationUtil.getStabilityColor(stability);

    // Styles
    static getTreeActiveStyle = ({ activeChild, activeOptions } = {}, fieldList, properties, thematicMaps = null) => {
        const customMap = (thematicMaps || []).find(thematicMap => thematicMap.id === activeChild);
        return StylesUtil.buildTreeActiveStyle(activeChild, activeOptions, fieldList, properties, customMap);
    }

    static getTreesActiveStyles = (layers, fieldList, thematicMaps) => {
        const stylesMap = new Map();
        if (!layers) return stylesMap;

        const { activeChild, activeOptions } = layers;
        const customMap = (thematicMaps || []).find(thematicMap => thematicMap.id === activeChild);
        const classification = ClassificationUtil.getBuiltInClassification(activeChild, fieldList);

        layers.eachLayer(layer => stylesMap.set(
            layer.feature.id,
            StylesUtil.buildTreeActiveStyle(activeChild, activeOptions, fieldList, layer.feature.properties, customMap, classification)
        ));

        return stylesMap;
    }

    static buildTreeActiveStyle = (activeChild, activeOptions, fieldList, properties, customMap, classification = ClassificationUtil.getBuiltInClassification(activeChild, fieldList)) => {
        const isRootProtection = activeChild === i18n.t("Protection racinaire");
        let style, customStyles;

        if (customMap) {
            customStyles = (customMap.styles || []).reduce((prevValue, customStyle) => ({ ...prevValue, [customStyle.type]: StylesUtil.getCustomStyleValue(customStyle, fieldList, properties) }), {});

            style = {
                radius: 5, color: '#000',
                fillColor: Object.hasOwn(customStyles, 'color')
                    ? (customStyles.color || ClassificationUtil.getUnmatchedColor(properties.category))
                    : 'var(--primary-100)',
                weight: 1, opacity: 1, fillOpacity: 0.8
            };
        } else if (isRootProtection) style = StylesUtil.getRootProtectionStyle(fieldList.zoomLevel, properties);
        else style = {
            radius: 5, color: '#000',
            fillColor: classification ? classification.getColor(classification.getKey(properties)) : StylesUtil.getTreeStyle().fillColor,
            weight: 1, opacity: 1, fillOpacity: 0.8
        };

        if (isRootProtection) style = { ...style, pane: 'trees', shape: 'circle' };
        else style = {
            ...style, pane: 'trees',
            fillColor: ClassificationUtil.isDisplayedAsEmptyLocation(activeChild, properties, customMap)
                ? ClassificationUtil.getEmptyLocationColor()
                : style.fillColor,
            radius: +customStyles?.radius || StylesUtil.getTreeRadius(fieldList.zoomLevel), shape: customStyles?.shape || 'circle'
        };

        if (activeOptions?.includes(i18n.t("Diamètre des couronnes")) && !['isEmpty', 'isStump', 'isChronoxyle'].some(property => properties[property])) {
            const crownDiameter = TreesUtil.getBiggestTrunkValueOf(properties, 'crownDiameter');
            style.radius = ((crownDiameter || 0) / 20000000) * Math.pow(2, fieldList.zoomLevel);
            if (style.radius < 5 || properties.toCutDown === 5) style.radius = 5;
        }
        if (activeOptions?.includes(i18n.t("Distinction mort/vif")) && properties.isDead) style.shape = 'diamond';
        if (style.shape.includes('star')) style.radius += 3;

        style.color = StylesUtil.getBorderColor(style);
        return StylesUtil.shareTreeStyle(style);
    }

    // Styles d'arbres mutualisés par contenu et gelés : ne jamais les modifier.
    static treeStyleCache = new Map();

    // Au-delà (ex. diamètre des couronnes, un rayon par arbre), on cesse d'alimenter le cache.
    static TREE_STYLE_CACHE_LIMIT = 512;

    static shareTreeStyle(style) {
        if (!style) return style;
        if (!TreesUtil.areMemoryOptimizationsEnabled()) return style;

        const names = Object.keys(style);
        let key = '';
        for (let i = 0; i < names.length; i++) {
            const value = style[names[i]];
            // Une valeur objet donnerait "[object Object]" : on ne mutualise pas.
            if (value !== null && typeof value === 'object') return style;
            key += names[i] + '' + value + '';
        }

        const shared = this.treeStyleCache.get(key);
        if (shared) return shared;

        if (this.treeStyleCache.size >= this.TREE_STYLE_CACHE_LIMIT) return style;

        const frozen = Object.freeze(style);
        this.treeStyleCache.set(key, frozen);
        return frozen;
    }

    static clearTreeStyleCache() {
        this.treeStyleCache.clear();
    }

    static getTreeRadius = (zoomLevel) => isMobile && zoomLevel >= SCALE_ZOOM_LEVEL ? (5 + (zoomLevel - SCALE_ZOOM_LEVEL) * 2) : 5;

    static getTreeStyle() {
        return {
            color: '#000', radius: 5,
            fillColor: 'var(--primary-100)',
            weight: 1, opacity: 1, fillOpacity: 0.8
        };
    }

    static getTreeHelpStyle() {
        return {
            color: 'var(--grey-120)', radius: 5, shape: 'circle',
            fillColor: 'var(--grey-100)', dashArray: '10 10',
            weight: 1, opacity: 1, fillOpacity: 0.8,
        };
    }

    static getTreeHighlightStyle({ activeOptions, activeChild } = {}, fieldList, properties, thematicMaps = null) {
        let radius = StylesUtil.getTreeRadius(fieldList.zoomLevel);

        if (activeOptions?.includes(i18n.t("Diamètre des couronnes")) && !['isEmpty', 'isStump', 'isChronoxyle'].some(property => properties[property])) {
            const crownDiameter = TreesUtil.getBiggestTrunkValueOf(properties, 'crownDiameter');
            radius = ((crownDiameter || 0) / 20000000) * Math.pow(2, fieldList.zoomLevel);
            if (radius < 5 || properties.toCutDown === 5) radius = 5;
        }
        if (activeChild === i18n.t("Protection racinaire")) {
            const rootProtectionStyle = StylesUtil.getRootProtectionStyle(fieldList.zoomLevel, properties);
            radius = rootProtectionStyle.radius + rootProtectionStyle.weight / 2;
        }

        const customMap = (thematicMaps || []).find(thematicMap => thematicMap.id === activeChild);
        let customStyles;
        if (customMap) customStyles = (customMap.styles || []).reduce((prevValue, style) => ({ ...prevValue, [style.type]: StylesUtil.getCustomStyleValue(style, fieldList, properties) }), {});

        let shape = 'circle';
        if (activeOptions?.includes(i18n.t("Distinction mort/vif")) && properties.isDead)
            shape = 'diamond';
        if (customStyles?.shape) shape = customStyles.shape;

        radius = customStyles?.radius ? (+customStyles.radius + 3) : radius + 3;
        if (shape.includes('star')) radius += 3;

        return {
            radius, shape, color: '#000',
            fillColor: '#78C2F7', weight: 1,
            opacity: 1, fillOpacity: 0.8
        };
    }

    static getTreeActionsUrgencyStyle(zoomLevel, actionsUrgency = 0) {
        return {
            radius: StylesUtil.getTreeRadius(zoomLevel), color: '#000',
            fillColor: ClassificationUtil.getActionsUrgencyColor(actionsUrgency),
            weight: 1, opacity: 1, fillOpacity: 0.8
        };
    }

    static getRootProtectionStyle(zoomLevel, properties) {
        const circumference = TreesUtil.getBiggestTrunkValueOf(properties, 'circumference') || 0;
        const [criticalRootProtection, sensibleRootProtection] = FormulasUtil.getRootProtection(circumference);
        let criticalRadius = (((criticalRootProtection * 100) + (circumference / (2 * Math.PI))) / 10000000) * Math.pow(2, zoomLevel);
        let sensibleRadius = (((sensibleRootProtection * 100) + (circumference / (2 * Math.PI))) / 10000000) * Math.pow(2, zoomLevel);
        if (sensibleRadius < 6) {
            criticalRadius = 5;
            sensibleRadius = 6;
        }

        const weight = sensibleRadius - criticalRadius;
        return { color: 'yellow', fillColor: 'red', opacity: 1, fillOpacity: 0.8, radius: criticalRadius + weight / 2, weight };
    }

    /* Espaces verts */
    // Styles
    static getGreenSpaceActiveStyle = (activeChild, fieldList, properties, thematicMaps = null) => {
        let style;

        const customMap = (thematicMaps || []).find(thematicMap => thematicMap.id === activeChild);
        if (customMap) {
            const customStyles = (customMap.styles || []).reduce((prevValue, customStyle) => ({ ...prevValue, [customStyle.type]: StylesUtil.getCustomStyleValue(customStyle, fieldList, properties) }), {});

            style = {
                fillColor: Object.hasOwn(customStyles, 'color') ? (customStyles.color || ClassificationUtil.getUnmatchedColor('Espace vert')) : 'var(--secondary-100)',
                color: '#000000', weight: 0.5, opacity: 0.4, fillOpacity: 0.6, fillPattern: null
            };
        } else {
            const classification = ClassificationUtil.getBuiltInClassification(activeChild, fieldList);
            if (!classification) style = StylesUtil.getGreenSpaceStyle();
            else {
                const key = classification.getKey(properties);
                style = {
                    fillColor: ClassificationUtil.getMissingDataColor(), fillPattern: null,
                    color: '#000000', weight: 0.5, opacity: 0.4,
                    fillOpacity: activeChild === i18n.t("Actions") ? 0.6 : 0.8,
                    ...(classification.getFill ? classification.getFill(key) : { fillColor: classification.getColor(key) })
                };
            }
        }

        return { ...style, pane: 'greenSpaces' };
    }

    static getGreenSpaceStyle() {
        return { fillColor: 'var(--secondary-100)', color: '#000000', weight: 0.5, opacity: 0.4, fillOpacity: 0.6, fillPattern: null };
    }

    static getGreenSpaceHelpStyle() {
        return { fillColor: 'var(--grey-100)', color: 'var(--grey-120)', weight: 4, opacity: 0.7, fillOpacity: 0.6, fillPattern: null };
    }

    static getGreenSpaceHighlightStyle() {
        return { pane: 'greenSpaces', fillColor: "#78C2F7", color: "#3EA8F4", weight: 8, opacity: 1, fillOpacity: 0.6, fillPattern: null };
    }

    static getGreenSpaceActionsUrgencyStyle(actionsUrgency = 0) {
        return { fillColor: StylesUtil.getActionsUrgencyColor(actionsUrgency), color: '#000000', weight: 0.5, opacity: 0.4, fillOpacity: 0.6, fillPattern: null };
    }

    /* Mobilier urbain */
    // Couleurs
    static getConditionColor = (condition) => ClassificationUtil.getConditionColor(condition);

    // Styles
    static getFurnitureIcon = (furnitureTypes, typeId) => {
        const type = furnitureTypes.find(type => type.id === typeId)?.label;

        const types = {
            [i18n.t("Lampadaire")]: faLampStreet,
            [i18n.t("Table")]: faTablePicnic,
            [i18n.t("Point d'eau")]: faPump,
            [i18n.t("Jeu pour enfants")]: faTeddyBear,
            [i18n.t("Autre")]: faQuestion,
            [i18n.t("Compteur d'eau")]: faTankWater,
            [i18n.t("Siège")]: faChair,
            [i18n.t("Sapin de Noël")]: faTreeDecorated,
            [i18n.t("Décor de Noël")]: faCandyCane,
            [i18n.t("Bac sol")]: faBoxTaped,
            [i18n.t("Bac suspendu")]: faSignHanging,
            [i18n.t("Nichoir")]: faFeather,
            [i18n.t("Panneau")]: faSignsPost,
            [i18n.t("Poubelle")]: faTrash
        };

        return types[type] || faBan;
    }

    static getFurnitureActiveStyle = (activeChild, fieldList, properties, thematicMaps = null) => {
        let style;

        const customMap = (thematicMaps || []).find(thematicMap => thematicMap.id === activeChild);
        let customStyles;
        if (customMap) {
            customStyles = (customMap.styles || []).reduce((prevValue, style) => ({ ...prevValue, [style.type]: StylesUtil.getCustomStyleValue(style, fieldList, properties) }), {});

            style = {
                radius: 6 * 0.6, color: Object.hasOwn(customStyles, 'color') ? (customStyles.color || ClassificationUtil.getUnmatchedColor('Mobilier')) : 'var(--blue-100)',
                fillColor: '#000',
                weight: 6 * 0.4, opacity: 1, fillOpacity: 0.8
            };
        }

        if (!customMap) {
            const classification = ClassificationUtil.getBuiltInClassification(activeChild, fieldList);
            style = classification
                ? { ...StylesUtil.getFurnitureStyle(fieldList.zoomLevel), color: classification.getColor(classification.getKey(properties)) }
                : StylesUtil.getFurnitureStyle(fieldList.zoomLevel);
        }

        style = { ...style, pane: 'furnitures', radius: customStyles?.radius ? +customStyles.radius * 0.6 : style.radius, weight: customStyles?.radius ? +customStyles.radius * 0.4 : style.weight, shape: customStyles?.shape || 'circle' };
        if (style.shape.includes('star')) style.radius += 3;

        style.color = StylesUtil.getBorderColor(style);
        return style;
    }

    static getFurnitureRadius = (zoomLevel) => isMobile && zoomLevel >= SCALE_ZOOM_LEVEL ? (6 + (zoomLevel - SCALE_ZOOM_LEVEL) * 2) : 6;

    static getFurnitureStyle(zoomLevel) {
        const totalRadius = StylesUtil.getFurnitureRadius(zoomLevel);
        return {
            radius: totalRadius * 0.6, color: 'var(--blue-100)',
            fillColor: '#000',
            weight: totalRadius * 0.4, opacity: 1, fillOpacity: 0.8
        };
    }

    static getFurnitureHelpStyle(zoomLevel) {
        return {
            radius: isMobile && zoomLevel >= SCALE_ZOOM_LEVEL ? (5 + (zoomLevel - SCALE_ZOOM_LEVEL) * 2) : 5, color: 'var(--grey-120)',
            fillColor: 'var(--grey-100)', dashArray: '10 10', shape: 'circle',
            weight: 1, opacity: 1, fillOpacity: 0.8
        };
    }

    static getFurnitureHighlightStyle(activeChild, fieldList, properties, thematicMaps = null) {
        const customMap = (thematicMaps || []).find(thematicMap => thematicMap.id === activeChild);
        let customStyles;
        if (customMap) customStyles = (customMap.styles || []).reduce((prevValue, style) => ({ ...prevValue, [style.type]: StylesUtil.getCustomStyleValue(style, fieldList, properties) }), {});

        let shape = 'circle';
        if (customStyles?.shape) shape = customStyles.shape;

        let radius = customStyles?.radius ? (+customStyles.radius + 1) : (isMobile && fieldList.zoomLevel >= SCALE_ZOOM_LEVEL ? (8 + (fieldList.zoomLevel - SCALE_ZOOM_LEVEL) * 2) : 8)
        if (shape.includes('star')) radius += 3;

        return {
            radius, shape, color: '#000',
            fillColor: '#78C2F7', weight: 1,
            opacity: 1, fillOpacity: 0.8
        };
    }

    static getFurnitureActionsUrgencyStyle(zoomLevel, actionsUrgency = 0) {
        const totalRadius = StylesUtil.getFurnitureRadius(zoomLevel);
        return {
            radius: totalRadius * 0.6, color: ClassificationUtil.getActionsUrgencyColor(actionsUrgency),
            fillColor: '#000',
            weight: totalRadius * 0.4, opacity: 1, fillOpacity: 0.8
        };
    }

    /* Repères */
    static getMarkerActiveStyle = (fieldList, feature) => {
        let style = StylesUtil.getMarkerStyle(feature, fieldList.zoomLevel);
        return { ...style, pane: 'markers', shape: 'square' };
    }

    static getMarkerStyle(feature, zoomLevel) {
        return {
            radius: isMobile && zoomLevel >= SCALE_ZOOM_LEVEL ? (5 + (zoomLevel - SCALE_ZOOM_LEVEL) * 2) : 5, color: '#000',
            fillColor: 'var(--pink-100)', weight: 1, opacity: 1, fillOpacity: 0.8
        };
    }

    static getMarkerHighlightStyle(fieldList, feature) {
        return {
            radius: (isMobile && fieldList.zoomLevel >= SCALE_ZOOM_LEVEL ? (5 + (fieldList.zoomLevel - SCALE_ZOOM_LEVEL) * 2) : 5) + 3, color: '#000',
            fillColor: '#78C2F7', weight: 1,
            opacity: 1, fillOpacity: 0.8
        };
    }

    static getMarkerHelpStyle(feature, zoomLevel) {
        return {
            radius: isMobile && zoomLevel >= SCALE_ZOOM_LEVEL ? (5 + (zoomLevel - SCALE_ZOOM_LEVEL) * 2) : 5, color: 'var(--grey-120)',
            fillColor: 'var(--grey-100)', dashArray: '10 10', shape: 'square',
            weight: 1, opacity: 1, fillOpacity: 0.8
        };
    }

    /* Stations */
    // Styles
    static getStationActiveStyle = (properties, fieldList) => {
        return { pane: 'stations-' + (properties?.level || 1), ...StylesUtil.getStationStyle(properties, fieldList) };
    }

    static getStationHighlightStyle(properties) {
        return { pane: 'stations-' + (properties?.level || 1), fillColor: '#ffffff', color: "#78C2F7", weight: 4, opacity: 1, fillOpacity: 0.1 };
    }

    static getStationStyle = (properties, fieldList) => {
        const classification = ClassificationUtil.getBuiltInClassification(i18n.t("Stations"), fieldList);
        return {
            fillColor: '#ffffff', color: classification.getColor(classification.getKey(properties || {})),
            weight: 3, opacity: 0.95, fillOpacity: 0.001
        };
    }

    static getStationColor = (level, fieldList) => ClassificationUtil.getStationColor(level, fieldList);

    /* Autre */
    static getSurroundingsLightStyle({ fillOpacity = 0.6, opacity = 0.8 } = {}) {
        return {
            fillColor: 'var(--grey-60)',
            color: 'var(--primary-100)',
            fillOpacity,
            weight: 5,
            opacity
        };
    }

    static getSurroundingsDarkStyle({ fillOpacity = 0.6, opacity = 0.8 } = {}) {
        return {
            fillColor: 'var(--black-60)',
            color: 'var(--primary-100)',
            fillOpacity,
            weight: 5,
            opacity
        };
    }

    static getPatterns = () => PatternsUtil.getPatterns();

    static getDropZoneStyle(dropZone, isDarkTheme = false) {
        let borderColor = 'var(--grey-100)';
        let textColor = 'rgba(189,189,189,1)';

        if (dropZone.isDragActive && dropZone.isDragAccept) {
            borderColor = 'var(--primary-100)';
            textColor = 'rgba(36,77,56,1)';
        } else if (dropZone.isDragActive && dropZone.isDragReject) {
            borderColor = 'var(--red-100)';
            textColor = 'rgba(102,14,22,1)';
        } else if (dropZone.isDragActive && dropZone.isTooManyItems) {
            borderColor = 'var(--yellow-100)';
            textColor = 'rgba(102,82,24,1)';
        }

        return {
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', padding: '20px', borderWidth: 2,
            borderRadius: 'var(--radius-md)', borderColor: borderColor, borderStyle: 'dashed',
            color: textColor, outline: 'none', backgroundColor: isDarkTheme ? 'var(--black-90)' : 'var(--white-80)',
            transition: 'border .24s ease-in-out'
        }
    }

    static getMapStyles() {
        const segmentStyle = {
            width: '100%', flexGrow: 1,
            border: 'none', borderRadius: '0',
            padding: '0', margin: '0'
        };

        const dimmerStyle = {
            width: '100%', flexGrow: 1,
            border: '0', borderRadius: '0',
            padding: '0', margin: '0',
            zIndex: 1100
        }

        const modalMobileStyle = {
            width: '100%', height: '100%',
            padding: 0, margin: 0,
            border: 'none', borderRadius: 0,
            flexFlow: 'column'
        }

        return { segmentStyle, dimmerStyle, modalMobileStyle };
    }


    // Toasts
    static getToastClassName(type) {
        return `app-toast app-toast--${type}`;
    }

    static getMentionsStyle() {
        return {
            control: {
                fontSize: 14,
                fontWeight: 'normal',
                padding: '9.5px 14px'
            },
            '&singleLine': {
                display: 'block',
                width: '100%',
                highlighter: {
                    padding: 1,
                    border: '2px inset transparent',
                },
                input: {
                    padding: '9.5px 14px',
                    border: '1px solid rgba(34, 36, 38, 0.15)',
                    borderRadius: '4px'
                },
            },
            suggestions: {
                backgroundColor: 'transparent',
                top: '16px',
                list: {
                    border: '1px solid white',
                    borderBottomLeftRadius: '4px',
                    borderBottomRightRadius: '4px',
                    maxHeight: '200px',
                    overflow: 'auto',
                    fontSize: 14,
                    backgroundColor: 'white',
                    className: 'mention-list'
                },
                item: {
                    padding: '8px 15px',
                    borderTop: '1px solid white',
                    '&focused': {
                        backgroundColor: 'rgba(0, 0, 0, 0.25)',
                    },
                },
            },
        };
    }
}